import { ref, uploadBytesResumable, getDownloadURL, FirebaseStorage } from 'firebase/storage';
import { getFirebaseStorage } from '../config/firebase';

export interface UploadVoiceMemoParams {
  audioBlob: Blob;
  coachId: string;
  coachName: string;
  patientId: string;
  domainId: string;
  memoTitle: string;
  durationSeconds: number;
  extension?: string;
  storageOverride?: FirebaseStorage;
  onProgress?: (percent: number) => void;
  uploadHandler?: (
    storagePath: string,
    audioBlob: Blob,
    metadata: any,
    onProgress?: (percent: number) => void
  ) => Promise<{ downloadUrl: string }>;
}

export interface UploadVoiceMemoResult {
  storagePath: string;
  downloadUrl: string;
  memoId: string;
  durationSeconds: number;
  format: string;
}

/**
 * Uploads a validated audio voice memo to Firebase Storage under:
 * voice_memos/{coachId}/{memoId}.{ext}
 *
 * Attaches metadata including coach ID, patient ID, domain, title, and duration.
 */
export async function uploadVoiceMemo(
  params: UploadVoiceMemoParams
): Promise<UploadVoiceMemoResult> {
  const {
    audioBlob,
    coachId,
    coachName,
    patientId,
    domainId,
    memoTitle,
    durationSeconds,
    extension = 'm4a',
    storageOverride,
    onProgress,
    uploadHandler,
  } = params;

  if (!audioBlob) {
    throw new Error('Cannot upload empty or null audio blob.');
  }

  const memoId = `memo_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  const storagePath = `voice_memos/${coachId}/${memoId}.${extension}`;

  const contentType =
    audioBlob.type ||
    (extension === 'webm' ? 'audio/webm' : extension === 'wav' ? 'audio/wav' : extension === 'mp3' ? 'audio/mpeg' : 'audio/mp4');

  const metadata = {
    contentType,
    customMetadata: {
      coachId,
      coachName,
      patientId,
      domainId,
      memoTitle,
      durationSeconds: durationSeconds.toFixed(1),
      uploadedAt: new Date().toISOString(),
    },
  };

  if (uploadHandler) {
    const handlerResult = await uploadHandler(storagePath, audioBlob, metadata, onProgress);
    return {
      storagePath,
      downloadUrl: handlerResult.downloadUrl,
      memoId,
      durationSeconds,
      format: extension,
    };
  }

  const storage = storageOverride || getFirebaseStorage();
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, audioBlob, metadata);

  return new Promise<UploadVoiceMemoResult>((resolve, reject) => {
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (snapshot.totalBytes > 0) {
          const percent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress?.(Math.min(100, Math.max(0, percent)));
        }
      },
      (error) => {
        reject(error);
      },
      async () => {
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({
            storagePath,
            downloadUrl,
            memoId,
            durationSeconds,
            format: extension,
          });
        } catch (err) {
          reject(err);
        }
      }
    );
  });
}
